import React, { memo, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { useDispatch } from 'react-redux';

import i18n from '../../assets/languages/configs';
import useLocalizedMoney from '../../hooks/useLocalizedMoney';
import { formatPriceWithMinMax, getValueByLanguage } from '../../utils';
import { localStorageKeys, PRICE_TYPE } from '../../utils/Constants';
import TextBreaks from '../TextBreaks/TextBreaks';
import styles from './styles.module.css';
import { MultilingualType, TicketInfo } from 'types';

type BuyTicketItemProps = {
    ticket: TicketInfo;
};

const BuyTicketItem = memo(({ ticket }: BuyTicketItemProps) => {
    const { t } = useTranslation();
    const navigate = useNavigate();
    const dispatch = useDispatch();
    const { formatMoney } = useLocalizedMoney();
    const { language } = i18n;
    const { isDisplayRemainingCount, maxParticipantCount, currentParticipantCount } = ticket;
    const remainingCount = maxParticipantCount != null ? maxParticipantCount - (currentParticipantCount ?? 0) : -1;

    // クリックしてチケット購入画面へ遷移
    const handleTicketClick = () => {
        if (maxParticipantCount != null && remainingCount <= 0) {
            return;
        }
        let path = '/buy/ticket?salesTicketId=';
        if (ticket.isReserve) {
            path = '/buy/reservation?salesTicketId=';
        }
        localStorage.removeItem(localStorageKeys.RESERVATION_FLOW);
        dispatch({
            type: 'RESERVATION_FLOW',
            payload: {
                currentIndex: 0,
            },
        });
        navigate(`${path}${ticket.salesTicketId}`);
    };
    const l = useCallback((text?: MultilingualType | null) => getValueByLanguage(text, language), [language]);

    /**
     * ユニット価格リスト情報をメモ化します。
     * チケットのユニット価格からユーザーに表示される情報を取得します。
     */
    const unitPriceList = ((ticket) => {
        if (ticket == null) {
            return [];
        }

        const result = [];
        if (ticket.isReserve && ticket.priceType === PRICE_TYPE.ADDITION) {
            const {
                priceAdultMin,
                priceAdultMax,
                priceCustom1Min,
                priceCustom1Max,
                priceCustom2Min,
                priceCustom2Max,
                priceCustom3Min,
                priceCustom3Max,
                isInUseAdult,
                isInUseCustom1,
                isInUseCustom2,
                isInUseCustom3,
            } = ticket;

            const priceConfigs = [
                {
                    minPrice: priceAdultMin,
                    maxPrice: priceAdultMax,
                    label: t('ticket-detail.adult'),
                    isInUse: isInUseAdult,
                },
                {
                    minPrice: priceCustom1Min,
                    maxPrice: priceCustom1Max,
                    label: l(ticket.custom1Name),
                    isInUse: isInUseCustom1,
                },
                {
                    minPrice: priceCustom2Min,
                    maxPrice: priceCustom2Max,
                    label: l(ticket.custom2Name),
                    isInUse: isInUseCustom2,
                },
                {
                    minPrice: priceCustom3Min,
                    maxPrice: priceCustom3Max,
                    label: l(ticket.custom3Name),
                    isInUse: isInUseCustom3,
                },
            ];

            priceConfigs.forEach(({ minPrice, maxPrice, label, isInUse }) => {
                if (isInUse) {
                    const price = formatPriceWithMinMax({ minPrice, maxPrice }, formatMoney);
                    result.push({ label, price });
                }
            });
        } else {
            if (ticket.isInUseAdult) {
                result.push({
                    label: t('ticket-detail.adult'),
                    price: ticket.priceAdult,
                });
            }
            if (ticket.isInUseCustom1) {
                result.push({
                    label: l(ticket.custom1Name),
                    price: ticket.priceCustom1,
                });
            }
            if (ticket.isInUseCustom2) {
                result.push({
                    label: l(ticket.custom2Name),
                    price: ticket.priceCustom2,
                });
            }
            if (ticket.isInUseCustom3) {
                result.push({
                    label: l(ticket.custom3Name),
                    price: ticket.priceCustom3,
                });
            }
        }

        return result;
    })(ticket);

    return (
        <div
            className={`${styles.ticketContent} ${
                maxParticipantCount != null && remainingCount <= 0 ? styles.disabled : ''
            }`}
            onClick={handleTicketClick}
        >
            <p className={styles.ticketName}>
                <TextBreaks text={l(ticket.ticketName)} />
            </p>
            <div>
                <div className={styles.ticketSubtitle}>
                    <TextBreaks text={l(ticket.subtitle)} />
                </div>
                {isDisplayRemainingCount && remainingCount > 0 && (
                    <div className={styles.ticketSubtitle}>{`${t('reservation.residue')}: ${remainingCount}`}</div>
                )}
                {maxParticipantCount != null && remainingCount <= 0 && (
                    <div className={styles.ticketSubtitle}>{t('reservation.sold-out')}</div>
                )}
                {(maxParticipantCount === null || remainingCount > 0) && (
                    <div className={styles.ticketPrice}>
                        {unitPriceList.map((item, index) => (
                            <span key={index} style={{ marginRight: 10 }}>
                                {item.label} /{' '}
                                {ticket.isReserve && ticket.priceType === PRICE_TYPE.ADDITION
                                    ? item.price
                                    : formatMoney(item.price, 'JPY')}
                            </span>
                        ))}
                    </div>
                )}
            </div>
        </div>
    );
});

export default BuyTicketItem;
